import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Breadcrumb, BreadcrumbItem, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaPlay, FaSave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { fetchDataApi, updateDataSourceApi, CreateDataSourceInput, DataSourceListItem } from 'src/services/dataSourceServices';

// Dummy data - same as in index.tsx
const dummyDataSources: any[] = [
  {
    id: "DS001",
    dataSourceName: "AWS EC2 Instances",
    assetType: "Service / Server",
    integrationMethod: "API Pull",
    updateFrequency: "Daily",
    clientId: "AWS_CLIENT_001",
    description: "Pull EC2 instance data from AWS API to track cloud server assets",
    status: "Active",
    lastRun: "2024-01-15 10:30:00",
    assetsCount: 25,
    createdAt: "2024-01-01T10:00:00Z",
    apiEndpoint: "https://ec2.us-east-1.amazonaws.com/",
    apiMethod: "GET",
    apiAuthentication: "AWS Signature v4"
  },
  {
    id: "DS002",
    dataSourceName: "Oracle Database Servers",
    assetType: "Database",
    integrationMethod: "Database",
    updateFrequency: "Weekly",
    clientId: "ORACLE_CLIENT_002",
    description: "Direct database connection to Oracle CMDB to extract database server information",
    status: "Active",
    lastRun: "2024-01-14 15:45:00",
    assetsCount: 12,
    createdAt: "2024-01-02T14:30:00Z",
    dbConnectionString: "jdbc:oracle:thin:@//cmdb.company.com:1521/CMDB",
    dbUsername: "cmdb_readonly",
    dbPassword: "secure_password_123"
  },
  {
    id: "DS003",
    dataSourceName: "Network Devices Inventory",
    assetType: "Network",
    integrationMethod: "File",
    updateFrequency: "Monthly",
    clientId: "NETWORK_CLIENT_003",
    description: "Import network device inventory from CSV file exported from network management system",
    status: "Active",
    lastRun: "2024-01-10 09:15:00",
    assetsCount: 45,
    createdAt: "2024-01-03T09:15:00Z",
    filePath: "/data/exports/network_devices.csv",
    fileFormat: "CSV"
  },
  {
    id: "DS004",
    dataSourceName: "Active Directory Users",
    assetType: "Endpoint",
    integrationMethod: "LDAP/Active Directory",
    updateFrequency: "Daily",
    clientId: "AD_CLIENT_004",
    description: "Extract user endpoint information from Active Directory for asset tracking",
    status: "Active",
    lastRun: "2024-01-15 08:00:00",
    assetsCount: 150,
    createdAt: "2024-01-04T11:45:00Z",
    ldapServer: "ldap.company.com",
    ldapPort: "389",
    ldapBaseDN: "dc=company,dc=com"
  },
  {
    id: "DS005",
    dataSourceName: "FTP Server Files",
    assetType: "Application",
    integrationMethod: "FTP",
    updateFrequency: "Weekly",
    clientId: "FTP_CLIENT_005",
    description: "Monitor application files and assets stored on FTP server",
    status: "Active",
    lastRun: "2024-01-13 14:20:00",
    assetsCount: 78,
    createdAt: "2024-01-05T16:20:00Z",
    ftpServer: "ftp.company.com",
    ftpPort: "21"
  }
];

const UpdateDataSource: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [useDummyData] = useState<boolean>(true);
  const [formData, setFormData] = useState<CreateDataSourceInput & { assetsCount?: number; lastRun?: string; status?: string }>({
    id: '',
    dataSourceName: '',
    assetType: '',
    integrationMethod: '',
    updateFrequency: '',
    clientId: '',
    description: '',
    assetsCount: 0,
    lastRun: '',
    status: 'Inactive',
  });

  useEffect(() => {
    loadDataSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDataSource = async () => {
    setLoading(true);
    try {
      if (useDummyData) {
        const data = dummyDataSources.find(ds => ds.id === id);
        if (!data) {
          toast.error(t('dataResource.update.toast.notFound', { defaultValue: 'Data source not found' }));
          navigate('/dataresource');
          return;
        }
        setFormData({
          id: data.id,
          dataSourceName: data.dataSourceName,
          assetType: data.assetType,
          integrationMethod: data.integrationMethod,
          updateFrequency: data.updateFrequency,
          clientId: data.clientId,
          description: data.description || '',
          filePath: data.filePath,
          fileFormat: data.fileFormat,
          apiEndpoint: data.apiEndpoint,
          apiMethod: data.apiMethod,
          apiAuthentication: data.apiAuthentication,
          dbConnectionString: data.dbConnectionString,
          dbUsername: data.dbUsername,
          dbPassword: data.dbPassword,
          ldapServer: data.ldapServer,
          ldapPort: data.ldapPort,
          ldapBaseDN: data.ldapBaseDN,
          ftpServer: data.ftpServer,
          ftpPort: data.ftpPort,
          assetsCount: data.assetsCount || 0,
          lastRun: data.lastRun || '',
          status: data.status || 'Inactive',
        });
      } else {
        const result = await fetchDataApi(`data-sources/${id}`);
        if (!result.success || !result.data) {
          toast.error(t('dataResource.update.toast.notFound', { defaultValue: 'Data source not found' }));
          navigate('/dataresource');
          return;
        }
        setFormData(result.data);
      }
    } catch (error: any) {
      toast.error(error.message || t('dataResource.update.toast.fetchFailed', { defaultValue: 'Failed to fetch data source' }));
      navigate('/dataresource');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dataresource');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!useDummyData) {
        await updateDataSourceApi({ ...formData, id: formData.id || id || '' });
      } else {
        // Simulate update for demo
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      toast.success(t('dataResource.update.toast.updated', { defaultValue: 'Data source updated successfully' }));
      navigate('/dataresource');
    } catch (error: any) {
      toast.error(error.message || t('dataResource.update.toast.fetchFailed', { defaultValue: 'Failed to update data source' }));
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (!formData) return;
    try {
      toast.info(t('dataResource.update.toast.running', { name: formData.dataSourceName, defaultValue: `Running ${formData.dataSourceName}...` }));
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulate API call

      const newAssetsCount = Math.floor(Math.random() * 50) + 10; // Random number between 10-60
      const currentAssetsCount = formData.assetsCount || 0;
      const updatedAssetsCount = currentAssetsCount + newAssetsCount;
      
      const updatedDataSource = {
        ...formData,
        lastRun: new Date().toLocaleString(),
        assetsCount: updatedAssetsCount,
        status: "Active"
      };
      setFormData(updatedDataSource);
      toast.success(t('dataResource.update.toast.runSuccess', { count: newAssetsCount, name: formData.dataSourceName, defaultValue: `Successfully imported ${newAssetsCount} assets from ${formData.dataSourceName}` }));
    } catch (error: any) {
      toast.error(error.message || t('dataResource.update.toast.runFailed', { message: error.message, defaultValue: 'Failed to run data source' }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (loading) {
    return (
      <div className="container-fluid" style={{ paddingTop: '100px', paddingBottom: '20px' }}>
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading', { defaultValue: 'Loading...' })}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ paddingTop: '100px', paddingBottom: '20px' }}>
      <Breadcrumb>
        <BreadcrumbItem>
          <a href="/">{t('common.home', { defaultValue: 'Home' })}</a>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <a href="/dataresource">{t('dataResource.list.titlePage', { defaultValue: 'Data Sources' })}</a>
        </BreadcrumbItem>
        <BreadcrumbItem active>
          {t('dataResource.update.header', { defaultValue: 'Update Data Source' })}
        </BreadcrumbItem>
      </Breadcrumb>

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <Button color="link" onClick={handleBack} className="p-0">
              <FaArrowLeft className="me-2" />
              {t('dataResource.update.back', { defaultValue: 'Back' })}
            </Button>
            <div className="vr"></div>
            <div>
              <h2 className="mb-0">{t('dataResource.update.header', { defaultValue: 'Update Data Source' })}</h2>
              <p className="text-muted mb-0 small">{t('dataResource.update.subheader', { defaultValue: 'Modify data source configuration and settings' })}</p>
            </div>
          </div>
          <Button color="success" onClick={handleRun}>
            <FaPlay className="me-2" />
            {t('dataResource.update.runIntegration', { defaultValue: 'Run Integration' })}
          </Button>
        </div>
      </div>

      <Form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <span className="badge bg-primary me-2">1</span>
                  {t('dataResource.update.basicInformation', { defaultValue: 'Basic Information' })}
                </h5>
              </div>
              <div className="card-body">
                <FormGroup>
                  <Label>{t('dataResource.update.labels.id', { defaultValue: 'Data Source ID' })} <span className="text-danger">*</span></Label>
                  <Input type="text" name="id" value={formData.id} onChange={handleChange} required disabled />
                </FormGroup>
                <FormGroup>
                  <Label>{t('dataResource.update.labels.name', { defaultValue: 'Data Source Name' })} <span className="text-danger">*</span></Label>
                  <Input type="text" name="dataSourceName" value={formData.dataSourceName} onChange={handleChange} required />
                </FormGroup>
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.assetType', { defaultValue: 'Asset Type' })} <span className="text-danger">*</span></Label>
                      <Input type="select" name="assetType" value={formData.assetType} onChange={handleChange} required>
                        <option value="">{t('dataResource.update.selects.assetType', { defaultValue: 'Select Asset Type' })}</option>
                        <option value="Server">Server</option>
                        <option value="Database">Database</option>
                        <option value="Network">Network</option>
                        <option value="Application">Application</option>
                        <option value="Endpoint">Endpoint</option>
                      </Input>
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.integrationMethod', { defaultValue: 'Integration Method' })} <span className="text-danger">*</span></Label>
                      <Input type="select" name="integrationMethod" value={formData.integrationMethod} onChange={handleChange} required>
                        <option value="">{t('dataResource.update.selects.integrationMethod', { defaultValue: 'Select Integration Method' })}</option>
                        <option value="File">File</option>
                        <option value="API Pull">API Pull</option>
                        <option value="API Push">API Push</option>
                        <option value="Database">Database</option>
                        <option value="LDAP/Active Directory">LDAP/Active Directory</option>
                        <option value="FTP">FTP</option>
                      </Input>
                    </FormGroup>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.updateFrequency', { defaultValue: 'Update Frequency' })} <span className="text-danger">*</span></Label>
                      <Input type="select" name="updateFrequency" value={formData.updateFrequency} onChange={handleChange} required>
                        <option value="">{t('dataResource.update.selects.updateFrequency', { defaultValue: 'Select Frequency' })}</option>
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                        <option value="Monthly">Monthly</option>
                      </Input>
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.clientId', { defaultValue: 'Client ID' })} <span className="text-danger">*</span></Label>
                      <Input type="text" name="clientId" value={formData.clientId} onChange={handleChange} required />
                    </FormGroup>
                  </div>
                </div>
                <FormGroup>
                  <Label>{t('dataResource.update.labels.description', { defaultValue: 'Description' })}</Label>
                  <Input type="textarea" name="description" value={formData.description} onChange={handleChange} rows={3} />
                </FormGroup>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <span className="badge bg-success me-2">2</span>
                  {t('dataResource.update.integrationConfiguration', { defaultValue: 'Integration Configuration' })}
                </h5>
              </div>
              <div className="card-body">
                {formData.integrationMethod === 'File' && (
                  <>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.filePath', { defaultValue: 'File Path' })}</Label>
                      <Input type="text" name="filePath" value={formData.filePath || ''} onChange={handleChange} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.fileFormat', { defaultValue: 'File Format' })}</Label>
                      <Input type="select" name="fileFormat" value={formData.fileFormat || ''} onChange={handleChange}>
                        <option value="">{t('dataResource.update.selects.fileFormat', { defaultValue: 'Select File Format' })}</option>
                        <option value="CSV">CSV</option>
                        <option value="JSON">JSON</option>
                        <option value="XML">XML</option>
                      </Input>
                    </FormGroup>
                  </>
                )}

                {(formData.integrationMethod === 'API Pull' || formData.integrationMethod === 'API Push') && (
                  <>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.apiEndpoint', { defaultValue: 'API Endpoint' })}</Label>
                      <Input type="text" name="apiEndpoint" value={formData.apiEndpoint || ''} onChange={handleChange} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.apiMethod', { defaultValue: 'API Method' })}</Label>
                      <Input type="select" name="apiMethod" value={formData.apiMethod || ''} onChange={handleChange}>
                        <option value="">{t('dataResource.update.selects.apiMethod', { defaultValue: 'Select API Method' })}</option>
                        <option value="GET">GET</option>
                        <option value="POST">POST</option>
                        <option value="PUT">PUT</option>
                        <option value="DELETE">DELETE</option>
                      </Input>
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.apiAuthentication', { defaultValue: 'Authentication' })}</Label>
                      <Input type="select" name="apiAuthentication" value={formData.apiAuthentication || ''} onChange={handleChange}>
                        <option value="">{t('dataResource.update.selects.apiAuthentication', { defaultValue: 'Select Authentication' })}</option>
                        <option value="Bearer Token">Bearer Token</option>
                        <option value="API Key">API Key</option>
                        <option value="Basic Auth">Basic Auth</option>
                        <option value="AWS Signature v4">AWS Signature v4</option>
                      </Input>
                    </FormGroup>
                  </>
                )}

                {formData.integrationMethod === 'Database' && (
                  <>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.dbConnectionString', { defaultValue: 'Connection String' })}</Label>
                      <Input type="text" name="dbConnectionString" value={formData.dbConnectionString || ''} onChange={handleChange} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.dbUsername', { defaultValue: 'Database Username' })}</Label>
                      <Input type="text" name="dbUsername" value={formData.dbUsername || ''} onChange={handleChange} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.dbPassword', { defaultValue: 'Database Password' })}</Label>
                      <Input type="password" name="dbPassword" value={formData.dbPassword || ''} onChange={handleChange} />
                    </FormGroup>
                  </>
                )}

                {formData.integrationMethod === 'LDAP/Active Directory' && (
                  <>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.ldapServer', { defaultValue: 'LDAP Server' })}</Label>
                      <Input type="text" name="ldapServer" value={formData.ldapServer || ''} onChange={handleChange} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.ldapPort', { defaultValue: 'LDAP Port' })}</Label>
                      <Input type="text" name="ldapPort" value={formData.ldapPort || ''} onChange={handleChange} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.ldapBaseDN', { defaultValue: 'Base DN' })}</Label>
                      <Input type="text" name="ldapBaseDN" value={formData.ldapBaseDN || ''} onChange={handleChange} />
                    </FormGroup>
                  </>
                )}

                {formData.integrationMethod === 'FTP' && (
                  <>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.ftpServer', { defaultValue: 'FTP Server' })}</Label>
                      <Input type="text" name="ftpServer" value={formData.ftpServer || ''} onChange={handleChange} />
                    </FormGroup>
                    <FormGroup>
                      <Label>{t('dataResource.update.labels.ftpPort', { defaultValue: 'FTP Port' })}</Label>
                      <Input type="text" name="ftpPort" value={formData.ftpPort || ''} onChange={handleChange} />
                    </FormGroup>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">{t('dataResource.update.sidebar.currentStatus', { defaultValue: 'Current Status' })}</h5>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">{t('dataResource.update.sidebar.lastRun', { defaultValue: 'Last Run' })}</span>
                  <span className="fw-bold">N/A</span>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span className="text-muted">{t('dataResource.update.sidebar.assetsCount', { defaultValue: 'Assets Count' })}</span>
                  <span className="fw-bold">0</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">{t('dataResource.update.sidebar.status', { defaultValue: 'Status' })}</span>
                  <span className="badge bg-success">Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button color="secondary" onClick={handleBack} disabled={saving}>
            {t('dataResource.update.actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button color="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {t('dataResource.update.actions.updating', { defaultValue: 'Updating...' })}
              </>
            ) : (
              <>
                <FaSave className="me-2" />
                {t('dataResource.update.actions.update', { defaultValue: 'Update Data Source' })}
              </>
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default UpdateDataSource;

